'use client';

/**
 * DSGVO-konformer Consent-Banner (Opt-in).
 *
 * Erscheint nur, wenn noch keine Entscheidung getroffen wurde
 * (Consent === 'unset'). Erst bei „Akzeptieren" werden Marketing-Pixel
 * (Meta/TikTok) geladen — siehe analytics.ts. „Ablehnen" speichert die
 * Ablehnung dauerhaft, sodass nichts geladen wird.
 *
 * Bewusst leichtgewichtig: kein TCF/IAB-Framework, keine externe
 * CMP-Dependency. Reicht für eine Landing mit genau zwei optionalen
 * Marketing-Pixeln + Link zur Datenschutzerklärung.
 */

import React, { useEffect, useState } from 'react';
import { getConsent, setConsent } from './analytics';

const ACCENT = '#9BC22E';
const ACCENT_TEXT = '#0A0D12';

export function ConsentBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (getConsent() === 'unset') setShow(true);
  }, []);

  if (!show) return null;

  const decide = (state: 'granted' | 'denied') => {
    setConsent(state);
    setShow(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie- und Tracking-Einwilligung"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 2147483000,
        maxWidth: 720,
        margin: '0 auto',
        background: 'rgba(10, 13, 18, 0.97)',
        backdropFilter: 'saturate(180%) blur(16px)',
        WebkitBackdropFilter: 'saturate(180%) blur(16px)',
        color: '#FFFFFF',
        border: '1px solid #1F252F',
        borderRadius: 16,
        padding: '18px 20px',
        boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 14,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", system-ui, sans-serif',
      }}
    >
      <div style={{ flex: '1 1 320px', minWidth: 240 }}>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>
          Cookies &amp; Reichweitenmessung
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.5, color: '#A6ACB5' }}>
          Wir nutzen optionale Marketing-Cookies (Meta, TikTok), um die
          Reichweite unserer Werbung zu messen. Erst mit deiner Zustimmung
          werden sie geladen. Mehr in der{' '}
          <a
            href="/datenschutz"
            style={{ color: ACCENT, textDecoration: 'underline' }}
          >
            Datenschutzerklärung
          </a>
          .
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, flex: '0 0 auto' }}>
        <button
          type="button"
          onClick={() => decide('denied')}
          style={{
            padding: '11px 18px',
            borderRadius: 10,
            background: 'transparent',
            border: '1.5px solid #2C3340',
            color: '#A6ACB5',
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          Ablehnen
        </button>
        <button
          type="button"
          onClick={() => decide('granted')}
          style={{
            padding: '11px 20px',
            borderRadius: 10,
            background: ACCENT,
            border: 'none',
            color: ACCENT_TEXT,
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          Akzeptieren
        </button>
      </div>
    </div>
  );
}
